import React, { useState } from 'react'
import { Dispatch, RootState } from '../../store/store';
import { connect } from 'react-redux';
import MetaFieldsV2 from '../MetaFieldsV2';
import PdfGenerator from '../ReportPages/PdfGenerator/PdfGeneratorV2';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';

interface ReportMetaFieldsV2Props extends ReturnType<typeof mapState>, ReturnType<typeof mapDispatch> {
  cycleReportId?: string;
}

const ReportMetaFieldsV2: React.FC<ReportMetaFieldsV2Props> = ({
  cycleReportId,
  createMetaField,
  deleteMetaField,
  updateListMetaFields,
  setMetaFields,
  metaFields,
  getMetaFields,
  saveMetaFieldsAsDefault,
  loadDefaultMetaFields,
}) => {
  const { cycleReportId: cycleReportIdParam } = useParams();
  const id = cycleReportId ?? cycleReportIdParam;
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const keepAsDefault = async () => {
    try {
      setIsLoading(true);

      await saveMetaFieldsAsDefault(id);
    } finally {
      setIsLoading(false);
    }
  }

  const loadDefault = async () => {
    try {
      setIsLoading(true);

      const metaFields = await loadDefaultMetaFields(id);
      setMetaFields(metaFields);

    } finally {
      setIsLoading(false);
    }
  }

  const buttons = [
    <PdfGenerator key="pdf-generator" />,
    <button
      key="save-default"
      onClick={keepAsDefault}
      className="inline-flex items-center justify-center rounded-md bg-meta-3 px-6 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
    >
      {t('buttons.saveAsDefault')}
    </button>,
    <button
      key="load-default"
      onClick={loadDefault}
      className="inline-flex items-center justify-center rounded-md bg-secondary px-6 text-center font-medium text-white hover:bg-opacity-90 disabled:opacity-50"
    >
      {t('buttons.loadDefaultMetaFields')}
    </button>
  ];

  if (!id) {
    return (
      <div className="c-default-meta-fields c-default-meta-fields_v2 mt-20 flex h-64 w-full items-center justify-center rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <SpinnerLoader />
      </div>
    );
  }

  return (
    <div className="c-default-meta-fields c-default-meta-fields_v2">
      <MetaFieldsV2
        id={id}
        createMetaField={createMetaField}
        deleteMetaField={deleteMetaField}
        metaFields={metaFields}
        onChangeListMetaFields={setMetaFields}
        title={t('labels.reportMetaFields')}
        updateListMetaFields={updateListMetaFields}
        getMetaFields={getMetaFields}
        isCustomLoading={isLoading}
        buttons={buttons}
      />
    </div>
  )
};

const mapState = (state: RootState) => ({
  metaFields: state.report_meta_fields.metaFields,
});

const mapDispatch = (dispatch: Dispatch) => ({
  createMetaField: dispatch.report_meta_fields.createMetaField,
  deleteMetaField: dispatch.report_meta_fields.deleteMetaField,
  updateListMetaFields: dispatch.report_meta_fields.updateListMetaFields,
  setMetaFields: dispatch.report_meta_fields.setList,
  getMetaFields: dispatch.report_meta_fields.getMetaFields,
  saveMetaFieldsAsDefault: dispatch.report_meta_fields.saveMetaFieldsAsDefault,
  loadDefaultMetaFields: dispatch.report_meta_fields.loadDefaultMetaFields,
});

export default connect(mapState, mapDispatch)(ReportMetaFieldsV2);
